/**
 * Typed API client for Catalog Variations — product variants, price types
 * and per-variation price lists.
 *
 * Mirrors internal/catalog. A variation belongs to a parent product; prices
 * are set per (variation, price-type) pair, where price types model customer
 * groups / currencies.
 */

import { apiClient } from '@/lib/apiClient';

export interface Variation {
  id: string;
  sku: string;
  name: string;
  attributes: unknown;
  price?: number | null;
  currency?: string | null;
  weightKg?: number | null;
  enabled: boolean;
}

export interface PriceType {
  id: string;
  code: string;
  name: string;
  customerGroup?: string | null;
  baseCurrency: string;
  isDefault: boolean;
}

export async function listVariations(parentProductId: string): Promise<Variation[]> {
  const { data } = await apiClient.get<Variation[]>(`/catalog/variations/of/${parentProductId}`);
  return data;
}

export interface CreateVariationRequest {
  parentProductId: string;
  sku: string;
  name: string;
  attributes?: unknown;
  price?: number;
  currency?: string;
  weightKg?: number;
}

export async function createVariation(req: CreateVariationRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/catalog/variations', req);
  return data;
}

export async function listPriceTypes(): Promise<PriceType[]> {
  const { data } = await apiClient.get<PriceType[]>('/catalog/variations/price-types');
  return data;
}

export interface CreatePriceTypeRequest {
  code: string;
  name: string;
  customerGroup?: string;
  baseCurrency?: string;
  isDefault?: boolean;
}

export async function createPriceType(req: CreatePriceTypeRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/catalog/variations/price-types', req);
  return data;
}

export async function setVariationPrice(
  variationId: string,
  priceTypeId: string,
  price: number,
  currency: string,
): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>(
    `/catalog/variations/${variationId}/prices/${priceTypeId}`,
    { price, currency },
  );
  return data;
}
